import {
  Supertype,
  supertypeClass,
  property,
  remote,
  Remoteable,
  Persistable,
  amorphicStatic,
} from '@haventech/amorphic';
import {
  IsomorphicPersistent,
  Persistent,
  IsomorphicQuery,
  Query,
  Isomorphic,
  Serializable,
  AppController,
  SubController,
  Everything,
} from '@hit/amorphic-types';
import { BaseController } from './BaseController';
import { Assumptions } from './static/Assumptions';
import { Utils } from './Utils';
import { AuthenticatedPrincipal } from './AuthenticatedPrincipal';
import { HavenAnalytics } from './HavenAnalytics';

import { ApplicationController } from './ApplicationController';
import { Principal } from './Principal';
import { CommonUtilsBackend } from './static/CommonUtilsBackend';
import { Category } from '../js/logger/CPLogger';
import { UICategory, UILoggerSingleton } from './logger/UILoggerSingleton';

declare var window;
declare var $;

@supertypeClass({ toClient: true, toServer: true })
export abstract class HITController extends BaseController {
  @property({ getType: () => ApplicationController })
  applicationController: ApplicationController;

  @property({ isLocal: true })
  heartBeatInterval: number;

  @property({ isLocal: true })
  shutdownInProgress: boolean;

  @property({ isLocal: true })
  activity: boolean = true;

  @property({ isLocal: true })
  pageSaved: boolean = true;

  @property({ isLocal: true })
  sessionExpiration: number;

  @property({ toSever: false })
  showErrorAlerts: boolean = false;

  @property({ isLocal: true })
  pendingPage: string;

  @property({ isLocal: true })
  waitCount: number;

  @property({ isLocal: true })
  pagesBeingTracked;

  @property({ isLocal: true })
  amorphicStatus: string;

  @property({ isLocal: true })
  doGetStarted: boolean;

  @property({ isLocal: true })
  currentFAQ: string;

  @property({ isLocal: true })
  currentResource: string;

  @property()
  page: string;

  @property()
  file: string;

  @property({ isLocal: true })
  popup: string;

  @property({ isLocal: true })
  popupDialogId: string;

  @property()
  error: string;

  @property()
  status: string;

  @property({ isLocal: true })
  amorphicLoaded: boolean = true;

  @property({ isLocal: true })
  isWaiting: boolean;

  @property({ isLocal: true })
  havenPhone: string = '1-855-744-2836';

  @property()
  loggedInUser: AuthenticatedPrincipal;

  @property()
  scrollTo;

  @property()
  messageRefresh: boolean;

  @property()
  customerMetric;

  @property()
  sub;

  constructor() {
    super();
  }

  init() {
    return this;
  }

  abstract handleShutdown(): void;

  @remote({ on: 'client' })
  publicSave() {}

  getPrincipal(): Principal {
    return this.customer;
  }

  setPrincipal(principal) {
    this.customer = principal;
  }

  isProductSoldIn(state) {
    //TODO: consolidate with product controllers using availableStatesRegistry
    return false;
  }

  displayError(str) {
    if (this.amorphic.config.nconf.get('environment') !== 'production' && this.showErrorAlerts) {
      alert(str);
    }
  }

  serverInit() {
    super.serverInit();
    this.showErrorAlerts = process.env.APP_ENV !== 'production' && process.env.DISABLE_ERROR_ALERTS !== 'true';
  }

  clientInit(sessionExpiration?) {
    super.clientInit();

    if (typeof this.bindster !== 'undefined') {
      this.bindster.alert = function (message) {
        const error = {
          isHumanRelated: false,
          message: message,
        };
        UILoggerSingleton.getInstance().error({
          module: 'HITController',
          function: 'bindster',
          category: UICategory.request,
          error: error,
        });
      }.bind(this);
    }

    if (typeof window.AmorphicRouter !== 'undefined') {
      this.router = window.AmorphicRouter;
      this.route = window.AmorphicRouter.route(this, window.havenRoutes);
    }

    // Manage automatic saving
    if (sessionExpiration) {
      this.sessionExpiration = sessionExpiration;
    }

    this.activity = true;

    if (this.heartBeatInterval) {
      clearInterval(this.heartBeatInterval);
    }

    // @ts-ignore
    this.heartBeatInterval = setInterval(
      function () {
        this.heartBeat();
      }.bind(this),
      1e3
    );

    this.pagesBeingTracked = {};
    // If refresh called explicity we don't count this as a render for saving page
    // Todo: Implement isDirty on RemoteObjectTemplate to deal with saving
    const refresh = this.refresh;

    this.refresh = function (_defer, hasChanges) {
      if (!this.optimizeRefresh || hasChanges) {
        this.messageRefresh = true;

        refresh.call(this);
      }

      this.optimizeRefresh = false;
    }.bind(this);

    const nonProductionEnvironment =
      typeof document !== 'undefined' && document.location.hostname.match(/^xyzzy|^localhost|^stage/);

    if (nonProductionEnvironment) {
      this.doGetStarted = true;
    }
  }

  handleRemoteError(error) {
    this.isWaiting = false;
    this.error = this.getErrorMessage(error);
  }

  pageInit(page) {
    this.password = '';
    this.newPassword = '';
    this.confirmPassword = '';
    this.error = '';
    this.scrollTo = page;
  }

  onrender(_name?) {
    this.scrollTo = null;

    if (this.messageRefresh) {
      this.messageRefresh = false;
    } else {
      this.registerActivity();
    }
  }

  registerActivity() {
    this.activity = true;
    this.pageSaved = false;
  }

  showPopup() {
    if (this.popup) {
      this.popupDialogId = '#' + 'modalDialog';

      $(this.popupDialogId).modal({
        show: true,
        backdrop: 'static',
        keyboard: false,
      });
    }
  }

  hidePopup() {
    if (this.popup) {
      // Don't fade out, to prevent opening another modal while fade-out transition is occurring.
      $(this.popupDialogId).removeClass('fade').modal('hide');
    }
  }

  scrollToHead() {
    $('html, body').animate(
      {
        scrollTop: 0,
      },
      'fast'
    );
  }

  isPage(name) {
    return this.page === name;
  }

  // Unused? Check, and remove either way
  log(level?, text?) {
    UILoggerSingleton.getInstance().info({}, 'Hit deprecated logger function, call a logger singleton directly');
  }

  getDisplayTime() {
    const date = new Date();

    return (
      date.getMonth() +
      1 +
      '/' +
      date.getDate() +
      '/' +
      date.getFullYear() +
      ' ' +
      date.toTimeString().replace(/ .*/, '')
    );
  }

  validateServerCall(functionName) {
    if (functionName.match(/^public/)) {
      return true;
    }

    return !!this.securityContext;
  }

  shutdown() {
    if (this.heartBeatInterval) {
      clearInterval(this.heartBeatInterval);
    }

    this.handleShutdown();

    this.hidePopup();
  }

  heartBeat() {
    if (this.activity && !this.pageSaved && window.amorphic['state'] === 'live') {
      this.savePage();
    }

    this.activity = false;

    if (
      this.heartBeat['videoStartTime'] > 0 &&
      !this.heartBeat['sentTenSecondEvent'] &&
      new Date().getTime() - this.heartBeat['videoStartTime'] > 100
    ) {
      this.heartBeat['sentTenSecondEvent'] = true;

      HavenAnalytics.send('event', {
        eventCategory: 'Homepage',
        eventAction: 'Engagement',
        eventLabel: 'Video begins',
        propertyName: 'Event',
        value: 'Video begins',
      });
    }

    if (
      this.heartBeat['videoStartTime'] > 0 &&
      !this.heartBeat['sentFortyFiveSecondEvent'] &&
      new Date().getTime() - this.heartBeat['videoStartTime'] > 5e4
    ) {
      this.heartBeat['sentFortyFiveSecondEvent'] = true;

      HavenAnalytics.send('event', {
        eventCategory: 'Homepage',
        eventAction: 'Engagement',
        eventLabel: '50 Seconds',
        propertyName: 'Event',
        value: '50 Seconds',
      });
    }
  }

  onchange() {
    if (typeof this.pagesBeingTracked[this.page] !== 'undefined') {
      this.pagesBeingTracked[this.page] = true;
    }
  }

  trackPageChanges() {
    this.pagesBeingTracked[this.page] = false;
  }

  untrackPageChanges() {
    delete this.pagesBeingTracked[this.page];
  }

  savePage() {
    if (this.shutdownInProgress) {
      return;
    }

    this.waitCount = 0;
    this.publicSave();
    this.pageSaved = true;

    return;
  }

  addCustomerMetric(metric) {
    for (let ix = 0; ix < this.customerMetric.length; ++ix) {
      if (this.customerMetric[ix].key === metric.key) {
        this.customerMetric[ix].slot = metric.slot;
        this.customerMetric[ix].ranges = metric.ranges;

        return;
      }
    }

    this.customerMetric.push(metric);
  }

  getCustomerMetric(metric) {
    for (let ix = 0; ix < this.customerMetric.length; ++ix) {
      if (this.customerMetric[ix].key === metric) {
        return this.customerMetric[ix];
      }
    }

    return null;
  }

  getCustomerMetrics() {
    const metrics = {};

    for (let ix = 0; ix < this.customerMetric.length; ++ix) {
      if (this.customerMetric[ix].value) {
        metrics[this.customerMetric[ix].key] = this.customerMetric[ix].value;
      }
    }

    return metrics;
  }

  getStateCode(stateVal) {
    for (const stateCode in Assumptions.stateValues) {
      if (Assumptions.stateValues.hasOwnProperty(stateCode)) {
        if (Assumptions.stateValues[stateCode] === stateVal) {
          return stateCode;
        }
      }
    }

    return null;
  }

  @remote({ on: 'server' })
  publicGetLocationByZip(zipcode?: string) {
    return CommonUtilsBackend.publicGetLocationByZip(zipcode);
  }

  getDripDetails(): { subscribed: boolean; signupEmail: string } {
    if (this['homeController']) {
      return {
        subscribed: this['homeController'].subscribed,
        signupEmail: this['homeController'].signupEmail,
      };
    }
    return <any>{};
  }
}
